import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
    Grid,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import MvrStatus from "./MvrStatus";

const HOT_PINK = "#ff0c9c";

function formatDob(emp) {
    if (!emp) return "";
    if (emp.dobDay && emp.dobMonth && emp.dobYear) {
        return `${String(emp.dobDay).padStart(2, "0")}/${String(emp.dobMonth).padStart(2, "0")}/${emp.dobYear}`;
    }
    return emp.dob || "";
}

function formatAddress(emp) {
    if (!emp) return "";
    const parts = [
        emp.addressLine1, emp.addressLine2, emp.addressLine3, emp.addressLine4,
        emp.suburb, emp.state, emp.postcode
    ].filter(Boolean);
    return parts.join(", ");
}

function parseApiError(e, fallback) {
    const raw = e?.response?.data;
    const data = raw?.response || raw;
    if (typeof data?.errors === "string" && data.errors) {
        return data.errors;
    }
    if (data?.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
        const msgs = Object.entries(data.errors).flatMap(([field, errs]) =>
            Array.isArray(errs) ? errs.map(m => `${field}: ${m}`) : [`${field}: ${errs}`]
        );
        if (msgs.length) return msgs.join(", ");
    }
    return data?.message || data?.error || e?.message || fallback;
}

@inject("hgSuperPayStore")
@observer
class HGSuperPayEmployeeModal extends Component {
    state = {
        modalMode: "create",
        modalEmployee: null,
        modalLoading: false,
        previewFailed: false,
        registrationSuccess: false,
        syncResult: null,
        modalError: null
    };

    componentDidMount() {
        this._mounted = true;
        if (this.props.open && this.props.verificationMode) this._loadEmployee();
    }

    componentWillUnmount() {
        this._mounted = false;
        this._previewRequestId = (this._previewRequestId || 0) + 1;
    }

    getPreviewKey = (props = this.props) =>
        JSON.stringify([props.contactId, props.employerProfileId, props.previewContextKey, !!props.verificationMode]);

    componentDidUpdate(prevProps) {
        if (this.props.open && (!prevProps.open || this.getPreviewKey(prevProps) !== this.getPreviewKey())) {
            this._loadEmployee();
        }
        if (!this.props.open && prevProps.open) {
            this._previewRequestId = (this._previewRequestId || 0) + 1;
            this._previewReadyFor = null;
            this.setState({
                modalMode: "create",
                modalEmployee: null,
                modalLoading: false,
                previewFailed: false,
                registrationSuccess: false,
                syncResult: null,
                modalError: null
            });
        }
    }

    _loadEmployee = () => {
        const { contactId, employerProfileId } = this.props;
        const requestId = (this._previewRequestId || 0) + 1;
        this._previewRequestId = requestId;
        this._previewReadyFor = null;
        const previewKey = this.getPreviewKey();
        const isCurrent = () => this._mounted && this.props.open &&
            requestId === this._previewRequestId && previewKey === this.getPreviewKey();
        if (!contactId || !employerProfileId) return;
        this.setState({ modalLoading: true, previewFailed: false, modalEmployee: null, modalError: null });
        this.props.hgSuperPayStore.getEmployeePreview(contactId, employerProfileId)
            .then(data => {
                if (!isCurrent()) return;
                const emp = data.employeeData || null;
                if (this.props.verificationMode && !emp) throw new Error("Employee details are unavailable. Please close and try again.");
                const missingFields = Array.isArray(data.missingFields) ? data.missingFields : [];
                const alreadySynced = data.status === "already_synced";
                // isCustom / custom bank fields may live at top level or nested under superAnnuationProfile
                const sap = emp ? (emp.superAnnuationProfile || emp) : {};
                const mapped = emp ? {
                    email: emp.email || null,
                    firstName: emp.firstName || null,
                    lastName: emp.lastName || null,
                    dobDay: emp.dobDay || null,
                    dobMonth: emp.dobMonth || null,
                    dobYear: emp.dobYear || null,
                    taxFileNumber: emp.taxFileNumber || null,
                    abn: emp.employeeAbn || null,
                    fundAbn: emp.fundAbn || null,
                    usi: emp.usi || null,
                    memberNumber: emp.memberNumber || null,
                    fundName: emp.fundName || null,
                    productName: emp.productName || null,
                    addressLine1: emp.addressLine1 || null,
                    suburb: emp.suburb || null,
                    state: emp.state || null,
                    postcode: emp.postcode || null,
                    country: emp.country || null,
                    isCustom: sap.isCustom || false,
                    esaAlias: sap.esaAlias || null,
                    bankAccountName: sap.bankAccountName || null,
                    bsb: sap.bsb || null,
                    bankAccountNumber: sap.bankAccountNumber || null,
                    missingFields,
                    alreadySynced,
                    registered: alreadySynced,
                    hasTfn: data.hasTfn !== false
                } : null;
                const modalMode = this.props.verificationMode ? "verify" : this.props.isMapped ? "update" : "create";
                this._previewReadyFor = previewKey;
                this.setState({ modalEmployee: mapped, modalLoading: false, modalMode });
            })
            .catch(err => {
                if (!isCurrent()) return;
                const msg = err?.response?.data?.message || err?.message || "Failed to load employee details.";
                this.setState({ modalLoading: false, previewFailed: true, modalError: msg });
            });
    };

    handleMvrCheck = () => {
        if (!this.props.open || !this.props.verificationMode || !this.props.canVerify ||
            this.props.verifying || this.state.modalLoading || this.state.previewFailed ||
            !this.state.modalEmployee || this._previewReadyFor !== this.getPreviewKey()) return;
        if (this.props.onVerifyMvr) this.props.onVerifyMvr();
    };

    handleRegister = () => {
        const { contactId, employerProfileId, onSuccess } = this.props;
        this.setState({ modalError: null, modalLoading: true });
        this.props.hgSuperPayStore.syncEmployee(contactId, employerProfileId)
            .then(result => {
                this.setState({ registrationSuccess: true, syncResult: result });
                if (onSuccess) onSuccess();
            })
            .catch(e => {
                this.setState({ modalError: parseApiError(e, "Sync failed. Please try again.") });
            })
            .finally(() => this.setState({ modalLoading: false }));
    };

    handleUpdate = () => {
        const { contactId, employerProfileId, onSuccess } = this.props;
        this.setState({ modalError: null, modalLoading: true });
        this.props.hgSuperPayStore.updateEmployee(contactId, employerProfileId)
            .then(result => {
                this.setState({ registrationSuccess: true, syncResult: { ...result, updated: true } });
                if (onSuccess) onSuccess();
            })
            .catch(e => {
                this.setState({ modalError: parseApiError(e, "Update failed. Please try again.") });
            })
            .finally(() => this.setState({ modalLoading: false }));
    };

    handleDone = () => {
        this.props.onClose();
    };

    render() {
        const { open, onClose, contactName, showActions, verificationMode, verifying } = this.props;
        const { modalMode, modalEmployee, modalLoading, previewFailed, registrationSuccess, syncResult, modalError } = this.state;
        const allowActions = !verificationMode && showActions !== false && (!this.props.isMapped || this.props.employeeAction === "update");
        const syncOutcome = syncResult && (
            syncResult.alreadySynced ? "Already synced" :
            syncResult.updated || syncResult.source === "local_mapping_updated" ? "Employee updated" :
            syncResult.source === "mygigsters_search" ? "Existing employee linked" :
            "Employee registered"
        );

        const displayName = contactName
            || (modalEmployee ? `${modalEmployee.firstName || ""} ${modalEmployee.lastName || ""}`.trim() : "")
            || "—";

        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby={verificationMode ? "mvr-preview-title" : undefined}>
                {!registrationSuccess && (
                    <Fragment>
                        <DialogTitle>
                            <Typography id={verificationMode ? "mvr-preview-title" : undefined} variant="title" style={{ fontWeight: 700 }}>
                                {verificationMode ? `Verify MVR: ${displayName}` : allowActions ? `${this.props.isMapped ? "Update" : "Sync"} Employee: ${displayName}` : `Super Account Details: ${displayName}`}
                            </Typography>
                            {verificationMode ? (
                                <Typography variant="caption" style={{ color: "#777" }}>
                                    Review this employee's details, then select MVR Check to verify their super fund membership.
                                </Typography>
                            ) : allowActions ? (
                                <Typography variant="caption" style={{ color: "#777" }}>
                                    {this.props.isMapped
                                        ? "Review this artist's Financial profile before updating their HG Super Pay details."
                                        : "Review this artist's Financial profile before syncing. HG Super Pay will register or link the employee as needed."}
                                </Typography>
                            ) : this.props.isMapped && showActions !== false && (
                                <Typography variant="caption" style={{ color: "#777" }}>
                                    Fund details are verified and no Financial profile changes are pending.
                                </Typography>
                            )}
                        </DialogTitle>
                        <DialogContent dividers style={verificationMode ? { overflowWrap: "anywhere" } : undefined}>
                            {modalLoading ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                                    <CircularProgress style={{ color: HOT_PINK }} />
                                </div>
                            ) : (
                                <Fragment>
                                    {!verificationMode && modalMode === "create" && modalEmployee && modalEmployee.alreadySynced && (
                                        <div style={{
                                            backgroundColor: "#e8f5e9",
                                            border: "1px solid #a5d6a7",
                                            borderRadius: 6,
                                            padding: "12px 16px",
                                            marginBottom: 20,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 10
                                        }}>
                                            <CheckCircleIcon style={{ color: "#2e7d32", marginTop: 2, flexShrink: 0 }} />
                                            <Typography variant="body2" style={{ color: "#1b5e20" }}>
                                                 <strong>{displayName}</strong> is already registered with HG Super Pay. Sync will check whether their details need updating.
                                            </Typography>
                                        </div>
                                    )}

                                    {!verificationMode && modalMode === "create" && (!modalEmployee || !modalEmployee.alreadySynced) && (
                                        <div style={{
                                            backgroundColor: "#e3f2fd",
                                            border: "1px solid #90caf9",
                                            borderRadius: 6,
                                            padding: "12px 16px",
                                            marginBottom: 20
                                        }}>
                                            <Typography variant="body2" style={{ color: "#1565c0" }}>
                                                This will sync <strong>{displayName}</strong>'s Financial profile to HG Super Pay.
                                            </Typography>
                                        </div>
                                    )}

                                    {modalEmployee && modalEmployee.missingFields && modalEmployee.missingFields.length > 0 && (
                                        <div style={{
                                            backgroundColor: "#fff3e0",
                                            border: "1px solid #ffb300",
                                            borderRadius: 6,
                                            padding: "12px 16px",
                                            marginBottom: 20,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 10
                                        }}>
                                            <WarningIcon style={{ color: "#e65100", marginTop: 2, flexShrink: 0 }} />
                                            <div>
                                                <Typography variant="body2" style={{ color: "#e65100", fontWeight: 600, marginBottom: 4 }}>
                                                    Missing required fields — {verificationMode ? "verification" : this.props.isMapped ? "update" : "registration"} may fail
                                                </Typography>
                                                <Typography variant="body2" style={{ color: "#e65100" }}>
                                                    {modalEmployee.missingFields.join(", ")}
                                                </Typography>
                                            </div>
                                        </div>
                                    )}

                                    {modalError && (
                                        <div style={{
                                            backgroundColor: "#fce4ec",
                                            border: "1px solid #e57373",
                                            borderRadius: 6,
                                            padding: "10px 16px",
                                            marginBottom: 16
                                        }}>
                                            <Typography variant="body2" style={{ color: "#c62828" }}>{modalError}</Typography>
                                        </div>
                                    )}
                                    {(this.props.isMapped !== undefined || this.props.mvrStatus != null) && (
                                        <div style={{ marginBottom: 20 }}>
                                            <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 6 }}>Fund verification</Typography>
                                            <MvrStatus status={this.props.mvrStatus} reason={this.props.mvrStatusReason} isMapped={this.props.isMapped} />
                                        </div>
                                    )}
                                    {allowActions && this.props.isMapped && (
                                        <Typography variant="body2" style={{ color: "#555", marginBottom: 20 }}>
                                            Financial profile changes are ready to send. Update will send the latest details for fund verification.
                                        </Typography>
                                    )}

                                    {modalEmployee && (
                                        <Grid container spacing={16}>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>First Name</Typography>
                                                <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.firstName || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Last Name</Typography>
                                                <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.lastName || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                            </Grid>
                                            <Grid item xs={12}><Divider /></Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                        Email
                                                    </Typography>
                                                    <Typography variant="body1" style={{ marginTop: 2 }}>
                                                        {modalEmployee.email ? (
                                                            <a
                                                                href={`mailto:${modalEmployee.email}`}
                                                                style={{ color: "#1976d2", textDecoration: "none" }}
                                                            >
                                                                {modalEmployee.email}
                                                            </a>
                                                        ) : (
                                                            <em style={{ color: "#ccc" }}>Not provided</em>
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Date of Birth</Typography>
                                                <Typography variant="body1" style={{ marginTop: 2 }}>{formatDob(modalEmployee) || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Employee ABN</Typography>
                                                <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.abn || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Tax File Number</Typography>
                                                {modalEmployee.hasTfn
                                                    ? <Typography variant="body1" style={{ marginTop: 2, letterSpacing: 2 }}>*********</Typography>
                                                    : <Typography variant="body2" style={{ marginTop: 2, color: "#e65100", fontStyle: "italic" }}>Not complete</Typography>
                                                }
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Address</Typography>
                                                <Typography variant="body1" style={{ marginTop: 2 }}>{formatAddress(modalEmployee) || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                            </Grid>
                                            <Grid item xs={12}><Divider /></Grid>
                                            {modalEmployee.isCustom ? (
                                                <Fragment>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Fund Name</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.fundName || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Fund ABN</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.fundAbn || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>ESA Alias</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.esaAlias || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Bank Account Name</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.bankAccountName || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>BSB</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.bsb || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Bank Account Number</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.bankAccountNumber || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                </Fragment>
                                            ) : (
                                                <Fragment>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Fund</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.fundName || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Product</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.productName || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>USI</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.usi || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Member Number</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.memberNumber || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" style={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Fund ABN</Typography>
                                                        <Typography variant="body1" style={{ marginTop: 2 }}>{modalEmployee.fundAbn || <em style={{ color: "#ccc" }}>Not provided</em>}</Typography>
                                                    </Grid>
                                                </Fragment>
                                            )}
                                        </Grid>
                                    )}
                                </Fragment>
                            )}
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button onClick={onClose} style={{ color: "#888" }}>
                                {allowActions || verificationMode ? "Cancel" : "Close"}
                            </Button>
                            {verificationMode && (
                                <Button
                                    variant="contained"
                                    disabled={modalLoading || previewFailed || !modalEmployee || !this.props.canVerify || verifying ||
                                        this._previewReadyFor !== this.getPreviewKey()}
                                    onClick={this.handleMvrCheck}
                                    style={{ backgroundColor: HOT_PINK, color: "#fff", fontWeight: 700 }}
                                >
                                    {verifying ? "Checking..." : "MVR Check"}
                                </Button>
                            )}
                            {allowActions && (
                                <Button
                                    variant="contained"
                                    disabled={modalLoading || previewFailed}
                                    onClick={this.props.employeeAction === "update" ? this.handleUpdate : this.handleRegister}
                                    style={{
                                        backgroundColor: !modalLoading ? HOT_PINK : undefined,
                                        color: !modalLoading ? "#fff" : undefined,
                                        fontWeight: 700
                                    }}
                                >
                                    {this.props.isMapped ? "Update employee" : "Sync employee"}
                                </Button>
                            )}
                        </DialogActions>
                    </Fragment>
                )}

                {registrationSuccess && (
                    <Fragment>
                        <DialogContent>
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "40px 20px"
                            }}>
                                <CheckCircleIcon style={{ color: "#43a047", fontSize: 64, marginBottom: 16 }} />
                                <Typography variant="title" style={{ fontWeight: 700, color: "#2e7d32", marginBottom: 8 }}>
                                    {syncOutcome}
                                </Typography>
                                <Typography variant="body2" style={{ color: "#555", textAlign: "center" }}>
                                    {syncResult && syncResult.employerRelinked
                                        ? `${displayName} was linked to this employer.`
                                        : `${displayName}'s ${this.props.isMapped ? "update" : "sync"} request completed.`}
                                </Typography>
                                <div style={{ marginTop: 20, textAlign: "center" }}>
                                    <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 6 }}>Fund verification</Typography>
                                    <MvrStatus status={syncResult && syncResult.mvrStatus} reason={syncResult && syncResult.mvrStatusReason} isMapped />
                                </div>
                            </div>
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px", justifyContent: "center" }}>
                            <Button
                                variant="contained"
                                onClick={this.handleDone}
                                style={{ backgroundColor: HOT_PINK, color: "#fff", fontWeight: 700 }}
                            >
                                Done
                            </Button>
                        </DialogActions>
                    </Fragment>
                )}
            </Dialog>
        );
    }
}

export default HGSuperPayEmployeeModal;
